import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Save, MessageSquare, Send } from 'lucide-react';
import { api } from '@/api/client';

interface Sms {
  username?: string;
  password?: string;
  user?: string;
  user2?: string;
  api_key?: string;
  peid?: string;
  templateid?: string;
  otp_templateid?: string;
  sms_company_name?: string;
  base_url?: string;
  enabled?: boolean;
}

interface SmsStats {
  total: number;
  sent: number;
  failed: number;
  sentToday: number;
  lastSentAt?: string | null;
  lastSentTo?: string | null;
  lastEvent?: string | null;
  lastAwbNo?: string | null;
}

const fmtIst = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Asia/Kolkata' }) + ' IST' : '—';

export function SmsSettingsPanel() {
  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: ['settings', 'sms'],
    queryFn: async () => {
      try { return (await api.get<Sms>('/settings/sms')).data ?? {}; }
      catch { return {} as Sms; }
    },
  });

  const { register, handleSubmit, reset, formState: { errors, isDirty } } = useForm<Sms>({ defaultValues: data ?? {} });

  useEffect(() => { if (data) reset(data); }, [data, reset]);

  const save = useMutation({
    mutationFn: async (d: Sms) => (await api.put('/settings/sms', d)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['settings', 'sms'] }); toast.success('SMS settings saved'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to save'),
  });

  // How many SMS the platform has actually sent, and when the last one went out. Read off sms_logs —
  // every send attempt is recorded there, so a silent gateway shows up as FAILED rather than as nothing.
  const { data: stats } = useQuery({
    queryKey: ['sms', 'stats'],
    queryFn: async () => (await api.get<SmsStats>('/sms/stats')).data,
    refetchInterval: 60_000,
  });

  const [testMobile, setTestMobile] = useState('');
  const sendTest = useMutation({
    mutationFn: async () => (await api.post('/sms/test', { mobile: testMobile })).data,
    onSuccess: (r: any) => {
      qc.invalidateQueries({ queryKey: ['sms', 'stats'] });
      if (r?.ok) toast.success(`Test SMS sent to ${testMobile}`);
      else toast.error(r?.skipped ?? r?.errorMessage ?? 'Gateway rejected the test SMS');
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Test send failed'),
  });

  return (
    <form onSubmit={handleSubmit((d) => save.mutate(d))} className="space-y-5 max-w-3xl">
      {/* Counters — asked for: how many SMS sent + last send date/time */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="SMS sent (total)" value={stats ? stats.sent.toLocaleString('en-IN') : '—'} />
        <Stat label="Sent today (IST)" value={stats ? stats.sentToday.toLocaleString('en-IN') : '—'} />
        <Stat label="Failed" value={stats ? stats.failed.toLocaleString('en-IN') : '—'} tone={stats && stats.failed > 0 ? 'bad' : undefined} />
        <Stat label="Last sent" value={fmtIst(stats?.lastSentAt)} small
          sub={stats?.lastSentTo ? `to ${stats.lastSentTo}${stats.lastAwbNo ? ` · ${stats.lastAwbNo}` : ''}` : undefined} />
      </div>

      {/* Test send — proves credentials + the DLT template in one call, using the real booking message */}
      <div className="border border-slate-200 rounded-lg p-3 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Send a test SMS to</label>
          <input value={testMobile} onChange={(e) => setTestMobile(e.target.value)} placeholder="10-digit mobile"
            className="input w-48" />
        </div>
        <button type="button" onClick={() => sendTest.mutate()} disabled={sendTest.isPending || testMobile.replace(/\D+/g, '').length < 10}
          className="px-4 py-2 border border-slate-300 rounded-lg text-sm font-medium hover:bg-slate-50 disabled:opacity-50 flex items-center gap-2">
          {sendTest.isPending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send test
        </button>
        <p className="text-[11px] text-slate-500 flex-1 min-w-[240px]">
          Sends the live "order shipped" template with a sample waybill — so a failure here means the
          gateway or the DLT template, not the booking code.
        </p>
      </div>
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
        <strong>DLT-compliant transactional SMS gateway.</strong> Used for OTP, AWB generation,
        out-for-delivery alerts, NDR alerts and remittance confirmation. PEID and Template ID come
        from your DLT registration on the operator's portal (Jio / Airtel / Vi).
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Field label="Username *" error={errors.username?.message}>
          <input {...register('username', { required: 'Required' })} placeholder="rkmgpj0" className="input" />
        </Field>
        <Field label="Password">
          <input type="password" {...register('password')} placeholder="(masked when saved)" className="input" />
        </Field>

        <Field label="Sender ID / User">
          <input {...register('user')} placeholder="GAINTL" className="input uppercase" />
        </Field>
        <Field label="Sender ID Alt / User2">
          <input {...register('user2')} placeholder="(optional fallback)" className="input uppercase" />
        </Field>

        <Field label="API Key">
          <input {...register('api_key')} placeholder="MSG91 auth key (UUID)" className="input font-mono text-xs" />
        </Field>
        <Field label="SMS Company Name">
          <input {...register('sms_company_name')} placeholder="GAINT LOGISTIC PVT LTD" className="input" />
        </Field>

        <Field label="PEID (Principal Entity ID)">
          <input {...register('peid')} placeholder="1701169512312112145" className="input font-mono text-xs" />
        </Field>
        <Field label="Default Template ID">
          <input {...register('templateid')} placeholder="1707173374058696955" className="input font-mono text-xs" />
        </Field>
        <Field label="OTP Template ID">
          <input {...register('otp_templateid')} placeholder="DLT id for the OTP template" className="input font-mono text-xs" />
          <p className="text-[10px] text-slate-400 mt-0.5">The OTP message is a different registered template from the booking one, so it needs its own DLT id. Blank falls back to the default.</p>
        </Field>

        <Field label="Gateway URL">
          <input {...register('base_url')} placeholder="https://sms.bulksmsind.in/v2/sendSMS" className="input font-mono text-xs" />
          <p className="text-[10px] text-slate-400 mt-0.5">Leave blank for the default. Params (username, message, sendername, smstype, numbers, apikey, peid, templateid) are appended automatically.</p>
        </Field>
        <Field label="Sending">
          <label className="flex items-center gap-2 text-sm text-slate-700 h-[38px]">
            <input type="checkbox" {...register('enabled')} className="rounded" defaultChecked />
            Send customer SMS on successful booking
          </label>
          <p className="text-[10px] text-slate-400">Uncheck to stop all outbound SMS without deleting the credentials.</p>
        </Field>
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600">
        <div className="font-medium text-slate-700 mb-1">Booking SMS (DLT template {data?.templateid ?? ''})</div>
        Your order <b>{'{AWB or LR}'}</b> has shipped! Track your shipment here : <b>https://trk.gaintlogistic.com/?awb_no={'{AWB or LR}'}</b> {data?.sms_company_name ?? 'GAINT LOGISTIC PVT LTD'}
        <div className="text-[11px] text-slate-500 mt-1">
          Sent to the customer's mobile the moment a booking succeeds — for our own network and every third-party
          courier. B2C quotes the AWB, B2B the LR.
        </div>
        <div className="font-medium text-slate-700 mt-3 mb-1">OTP SMS (DLT template {data?.otp_templateid ?? '— not set —'})</div>
        Your OTP for verifying your account with Gaint Logistics is <b>{'{OTP}'}</b>. This OTP is valid for 10 minutes. Do not share it with anyone.
        <div className="text-[11px] text-slate-500 mt-1">
          Sent when a merchant is created and whenever a mobile number is changed. The number cannot be saved until the OTP is entered back.
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 border-t border-slate-100 pt-4">
        <button type="button" onClick={() => reset(data ?? {})} disabled={!isDirty} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg disabled:opacity-50">Reset</button>
        <button type="submit" disabled={save.isPending || !isDirty}
          className="px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary-700 transition disabled:opacity-60 flex items-center gap-2 text-sm">
          {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Save SMS Settings
        </button>
      </div>

      <div className="text-xs text-slate-400 flex items-center gap-1">
        <MessageSquare size={12} /> Per-event template IDs (OTP / shipped / delivered / NDR) configurable in Phase 14.
      </div>
    </form>
  );
}

function Stat({ label, value, sub, small, tone }: { label: string; value: string; sub?: string; small?: boolean; tone?: 'bad' }) {
  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-white">
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`font-semibold ${small ? 'text-sm' : 'text-xl'} ${tone === 'bad' ? 'text-danger' : 'text-slate-900'} mt-0.5`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function Field({ label, children, error }: { label: string; children: React.ReactNode; error?: string }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      {children}
      {error && <p className="text-danger text-xs mt-1">{error}</p>}
    </div>
  );
}
